import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

export const Footer: React.FC = () => {
  const { t } = useTranslation(['landing']);
  return (
    <Box
      sx={{
        backgroundColor: '#002631',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        px: 6,
        pt: '65px',
        pb: '64px',
      }}
    >
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
            gap: 6,
            mb: 16,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#ffffff',
                letterSpacing: '-0.6px',
                lineHeight: 1.33,
              }}
            >
              MUU Agrotech
            </Typography>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 500,
                fontSize: '1rem',
                color: '#79a8ba',
                lineHeight: 1.5,
              }}
            >
              {t('landing:footer_new.description')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '9999px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                }}
              >
                <InstagramIcon sx={{ color: '#79a8ba', fontSize: 20 }} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '9999px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                }}
              >
                <LinkedInIcon sx={{ color: '#79a8ba', fontSize: 20 }} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, pb: 6 }}>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 500,
                fontSize: '1rem',
                color: '#8cf3f3',
                lineHeight: 1.5,
              }}
            >
              {t('landing:footer_new.columns.solutions.title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(t('landing:footer_new.columns.solutions.links', { returnObjects: true }) as string[]).map((link: string, index: number) => (
                <Typography
                  key={index}
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#79a8ba',
                    lineHeight: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#ffffff',
                    },
                  }}
                >
                  {link}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, pb: 6 }}>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 500,
                fontSize: '1rem',
                color: '#8cf3f3',
                lineHeight: 1.5,
              }}
            >
              {t('landing:footer_new.columns.company.title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(t('landing:footer_new.columns.company.links', { returnObjects: true }) as string[]).map((link: string, index: number) => (
                <Typography
                  key={index}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#79a8ba',
                    lineHeight: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#ffffff',
                    },
                  }}
                >
                  {link}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, pb: 13 }}>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 400,
                fontSize: '1rem',
                color: '#8cf3f3',
                lineHeight: 1.5,
              }}
            >
              Contato
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ color: '#79a8ba', fontSize: 20 }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#79a8ba',
                    lineHeight: 1.5,
                    pl: 1.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                contato@muu.agr.br
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ color: '#79a8ba', fontSize: 20 }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#79a8ba',
                    lineHeight: 1.5,
                    pl: 1.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                Av. Brigadeiro Faria Lima, 1811, Sala 1119, São Paulo-SP, CEP: 01452-001
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            pt: 8,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '0.75rem',
                color: 'rgba(121,168,186,0.6)',
                letterSpacing: '0.24px',
                lineHeight: 1,
              }}
            >
              2024 MUU Agrotech. All rights reserved. Data Intelligence for a Sustainable Future.
            </Typography>
            <Box sx={{ display: 'flex', gap: 8 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: 'rgba(121,168,186,0.6)',
                  letterSpacing: '0.24px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#79a8ba',
                  },
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: 'rgba(121,168,186,0.6)',
                  letterSpacing: '0.24px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#79a8ba',
                  },
                }}
              >
                Terms of Service
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: 'rgba(121,168,186,0.6)',
                  letterSpacing: '0.24px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#79a8ba',
                  },
                }}
              >
                Cookie Settings
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};